/**
 * Phase D — useOnboardingAttachStatus hook (Web)
 *
 * Direct port of mobile/src/lib/useOnboardingAttachStatus.js.
 * Polls GET /teams/my?envelope=v2 every 2s while
 * still_onboarding === true. Caps total poll duration at 60s
 * before bailing with a `timeout` flag so the UI can show a
 * "still onboarding, try again later" + manual retry CTA.
 *
 * On transient HTTP errors the poll backs off exponentially
 * (2s → 4s → 8s, max 8s) so we don't hammer the server.
 *
 * Returns `status === 'complete'` for users who don't have a
 * `still_onboarding` field in the response (existing pre-Phase-D
 * accounts) — so callers can treat the hook as a no-op for
 * non-fresh-signups.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { teamAPI } from '../lib/api';

const POLL_INTERVAL_MS = 2000;
const MAX_TOTAL_MS = 60_000;
const ERROR_BACKOFF_MS = [2000, 4000, 8000];

export function useOnboardingAttachStatus({ enabled = true } = {}) {
  const [status, setStatus] = useState('idle');
  const [attached, setAttached] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  const timeoutRef = useRef(null);
  const startedAtRef = useRef(null);
  const errorCountRef = useRef(0);
  const cancelledRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async () => {
    if (cancelledRef.current) return;
    if (startedAtRef.current && Date.now() - startedAtRef.current > MAX_TOTAL_MS) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await teamAPI.myTeams({ envelope: 'v2' });
      const body = response.data || {};
      // Defensive: if backend returns the legacy shape (no
      // still_onboarding key), treat as complete.
      if (!('still_onboarding' in body)) {
        setStatus('complete');
        return;
      }
      setAttached(body.attached || 0);
      setTotal(body.total || 0);
      if (body.still_onboarding) {
        setStatus('in_progress');
        errorCountRef.current = 0;
        timeoutRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
      } else {
        setStatus('complete');
      }
    } catch (e) {
      errorCountRef.current += 1;
      setError(e?.response?.data || e?.message || 'poll_failed');
      const idx = Math.min(errorCountRef.current - 1, ERROR_BACKOFF_MS.length - 1);
      const delay = ERROR_BACKOFF_MS[idx];
      if (startedAtRef.current && Date.now() - startedAtRef.current > MAX_TOTAL_MS) {
        setStatus('timeout');
        return;
      }
      timeoutRef.current = setTimeout(pollOnce, delay);
    }
  }, []);

  const retry = useCallback(() => {
    clearTimer();
    startedAtRef.current = Date.now();
    errorCountRef.current = 0;
    setError(null);
    setStatus('in_progress');
    pollOnce();
  }, [clearTimer, pollOnce]);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }
    cancelledRef.current = false;
    startedAtRef.current = Date.now();
    setStatus('checking');
    pollOnce();
    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
  }, [enabled, pollOnce, clearTimer]);

  return { status, attached, total, error, retry };
}

export default useOnboardingAttachStatus;
